import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import _partial from 'lodash/partial'
import _isFunction from 'lodash/isFunction'
import _concat from 'lodash/concat'
import _join from 'lodash/join'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import { connect as connectStaticText } from 'components/StaticText/connect'
import { getBoundActions } from 'actions'
import Button from 'components/Button'
import { formatSeriesHost } from 'theme/web-app'
import VideoPlayerMetaMore from 'components/VideoPlayerMetaMore'
import TextSeasonEpisode from 'components/TextSeasonEpisode'
import { isTypeYogaFitnessEpisode, isTypeYogaFitnessVideo } from 'services/content-type'
import { NotificationsFollowButton, BUTTON_TYPES } from 'components/NotificationsFollowButton'
import { SUBSCRIPTION_TYPES } from 'services/notifications'
import WithAuth from 'components/WithAuth'
import { TYPE_SHARE_V2_SHARE } from 'services/dialog'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { List } from 'immutable'
import { isEntitled } from 'services/subscription'
import { H3, H2 } from 'components/Heading'

const iconShareClass = ['icon--share']

function closeShareDialog (props) {
  const { clearShare } = props
  clearShare()
}

function getClassName (props) {
  const className = _concat(['video-player-meta'], props.className || [])
  if (props.visible) {
    className.push('video-player-meta--visible')
  }
  return _join(className, ' ')
}

function onClickMore (e, autoHide, setAutoHide, toggleMoreVisible) {
  if (_isFunction(setAutoHide)) {
    setAutoHide(!autoHide)
  }
  if (_isFunction(toggleMoreVisible)) {
    toggleMoreVisible()
  }
}

function renderVideoPlayerMetaMore (props) {
  return (
    <VideoPlayerMetaMore
      description={props.description}
      visible={props.moreVisible}
      onClick={_partial(
        onClickMore,
        _partial.placeholder,
        props.autoHide,
        props.setAutoHide,
        props.toggleMoreVisible,
      )}
    />
  )
}

function VideoPlayerMeta (props) {
  const {
    series,
    seriesId,
    host,
    title,
    copyright,
    contentType,
    season,
    episode,
    description,
    isShareable,
    staticText,
    playerPlayOrPause,
    playerPlay,
    playerPause,
    isPlaying,
    auth,
  } = props
  const userIsEntitled = isEntitled(auth.get('subscriptions', List()))
  const isYogaFitnessMeditationEpisode = isTypeYogaFitnessEpisode(contentType)
  const isYogaFitnessMeditationVideo = isTypeYogaFitnessVideo(contentType)
  const isFullscreen = document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement

  const renderShareModal = useCallback(() => {
    const wasPlaying = isPlaying()
    playerPause()
    props.renderModal(TYPE_SHARE_V2_SHARE, {
      onDismiss: () => {
        if (wasPlaying) playerPlay()
        closeShareDialog(props)
      },
      contentId: props.id,
      type: 'VIDEO',
      enableTime: true,
      title: props.title,
    })
  }, [])

  return (
    <div className={getClassName(props)} onClick={playerPlayOrPause}>
      <H2 inverted className="video-player-meta__title">{title}</H2>
      <H3 inverted className="video-player-meta__series-host">
        {formatSeriesHost(series, host)}
      </H3>
      <div className="video-player-meta__meta-details">
        {
          !isYogaFitnessMeditationEpisode && !isYogaFitnessMeditationVideo
            ? <span className="video-player-meta__season-episode">{copyright}</span>
            : null
        }
        <TextSeasonEpisode
          className={['video-player-meta__season-episode']}
          season={season}
          episode={episode}
        />
      </div>
      {description && renderVideoPlayerMetaMore(props)}
      <WithAuth>
        {seriesId
          ? (<NotificationsFollowButton
            subscriptionType={SUBSCRIPTION_TYPES.SERIES}
            type={BUTTON_TYPES.LINK}
            contentId={seriesId}
          />)
          : null}
      </WithAuth>
      {isShareable && !isFullscreen && userIsEntitled ?
        <WithAuth>
          <div className="video-player-meta__btn-wrapper">
            <Button
              text={staticText.getIn(['data', 'share'])}
              buttonClass={[
                'button--ghost',
                'button--with-icon',
                'button--stacked',
                'video-player-meta__action-button',
              ]}
              onClick={renderShareModal}
              iconClass={iconShareClass}
            />
          </div>
        </WithAuth> : null
      }
    </div>
  )
}

VideoPlayerMeta.propTypes = {
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  series: PropTypes.string,
  seriesId: PropTypes.number,
  host: PropTypes.string,
  season: PropTypes.number,
  episode: PropTypes.number,
  copyright: PropTypes.string,
  contentType: PropTypes.string,
  description: PropTypes.string,
  geoAvailibility: PropTypes.string.isRequired,
  visible: PropTypes.bool,
  moreVisible: PropTypes.bool,
  className: PropTypes.array,
  toggleMoreVisible: PropTypes.func,
  setAutoHide: PropTypes.func,
  playerPause: PropTypes.func.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
}

export default compose(
  connectStaticText({ storeKey: 'videoPlayerMeta' }),
  connectRedux(
    (state) => {
      return {
        isShareable: state.media.getIn(['data', 'isShareable'], false),
        auth: state.auth,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        renderModal: actions.dialog.renderModal,
        clearShare: actions.share.clearShare,
      }
    },
  ))(VideoPlayerMeta)
